import {
    IIntegrationResponse,
    ILocationResponse,
    IntegrationResponse,
    IntegrationUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { Detail } from 'components/detail/detail';
import {
    DetailsColumns,
    DetailsContainer,
    DetailsSection,
    Section,
} from 'components/detail/styled';
import { StatusLozengeInlineEditor } from 'components/inline_edit/StatusLozengeInlineEditor';
import { TextAreaInlineEditor } from 'components/inline_edit/TextAreaInlineEditor';
import { NoValue } from 'components/no_value/NoValue';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { sdClient } from 'contexts/SDKClientContext';
import { useActiveIntegration } from 'custom_hooks/data_hooks/integrations/useActiveIntegration';
import { Spacer } from 'pages/Demo/utils';
import {
    IntegrationStatusConfirmationModal,
    StatusUpdateInfo,
} from 'pages/Settings/Integrations/IntegrationStatusConfirmationModal';
import { IntegrationMaxChar } from 'pages/Settings/Integrations/shared';
import { IntegrationState } from 'pages/Settings/Integrations/types';
import { useState } from 'react';
import { useThemeValues } from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';
import { updateIntegration } from 'utils/integration';

export type IntegrationDetailsContentProps = Readonly<{
    location: ILocationResponse;
    integration: IIntegrationResponse;
    onUpdate: () => void;
}>;

export function IntegrationDetailsContent({
    location,
    integration,
    onUpdate,
}: IntegrationDetailsContentProps) {
    const { secondaryText } = useThemeValues();

    const ActiveStatusOption = {
        status: 'Active',
        color: KCFColor.green2,
        value: IntegrationState.Active,
    };
    const InactiveStatusOption = {
        status: 'Inactive',
        color: secondaryText,
        value: IntegrationState.Inactive,
    };
    const StatusOptions = [ActiveStatusOption, InactiveStatusOption];
    type StatusOption = typeof StatusOptions[number];

    const [
        destructiveUpdateInfo,
        setDestructiveUpdateInfo,
    ] = useState<StatusUpdateInfo>();

    const statusString = integration.Active ? 'Active' : 'Inactive';
    const [currentStatus, setCurrentStatus] = useState<string>(statusString);
    const { data: activeIntegration } = useActiveIntegration(
        location.Id,
        integration.IntegrationType
    );

    const { mutate: updateIntegrationNotes, isPending: isUpdatingNotes } = useMutation({
        mutationFn: (updatedNotes: string | undefined) =>
            sdClient.updateIntegrationForLocation({
                id: location.Id,
                integrationId: integration.Id,
                body: new IntegrationUpdateRequest({
                    SoftwareName: integration.SoftwareName,
                    Notes: updatedNotes,
                }),
            }),

        onSuccess: onUpdate,
    });

    const { mutate: updateIntegrationStatus, isPending: isUpdatingStatus } = useMutation({
        mutationFn: (updatedStatus: IntegrationState) => {
            if (activeIntegration && activeIntegration.Id !== integration.Id) {
                setDestructiveUpdateInfo({
                    locationId: location.Id,
                    integration: new IntegrationResponse(integration),
                    affectedIntegration: activeIntegration,
                });
                return Promise.resolve();
            }
            return updateIntegration({
                locationId: location.Id,
                integration: new IntegrationResponse(integration),
                updatedIntegration: new IntegrationUpdateRequest({
                    Active: updatedStatus === IntegrationState.Active,
                    SoftwareName: integration.SoftwareName,
                }),
            });
        },

        onSuccess: onUpdate,
    });

    const isUpdating = isUpdatingNotes || isUpdatingStatus;

    const onSaveStatus = (updatedStatus: StatusOption | undefined) => {
        const status = updatedStatus ?? InactiveStatusOption;
        updateIntegrationStatus(status.value);
        if (!destructiveUpdateInfo) {
            setCurrentStatus(status.status);
        }
    };

    const onConfirmDestructiveSave = () => {
        setDestructiveUpdateInfo(undefined);
        setCurrentStatus(statusString);
        onUpdate();
    };

    return (
        <DetailsContainer>
            <SpinnerArea isLoading={isUpdating}>
                <Section>
                    <Spacer>
                        <StatusLozengeInlineEditor
                            currentStatus={currentStatus}
                            options={StatusOptions}
                            isDisabled={isUpdating}
                            onSave={onSaveStatus}
                        />
                    </Spacer>
                </Section>
                <DetailsColumns>
                    <DetailsSection>
                        <Detail name="Integration Type">
                            {integration.IntegrationType.toUpperCase()}
                        </Detail>
                        <Detail name="Software Name">{integration.SoftwareName}</Detail>
                    </DetailsSection>
                    <DetailsSection>
                        <Detail name="Created Date">
                            <DateRelativeToNow date={integration.Created} />
                        </Detail>
                        <Detail name="Last Update Date">
                            <DateRelativeToNow date={integration.Updated} />
                        </Detail>
                    </DetailsSection>
                    <DetailsSection>
                        <Detail name="Created By">
                            {integration.CreatedBy.UserFirstName}{' '}
                            {integration.CreatedBy.UserLastName}
                        </Detail>
                        <Detail name="Notes">
                            <TextAreaInlineEditor
                                valueStyle={{
                                    overflowY: 'auto',
                                    maxHeight: '20vh',
                                    lineHeight: 'normal',
                                    alignSelf: 'flex-start',
                                }}
                                renderValue={(value) => value || <NoValue />}
                                value={integration.Notes}
                                onConfirm={updateIntegrationNotes}
                                isDisabled={isUpdating}
                                validate={(updatedNotes) =>
                                    updatedNotes &&
                                    updatedNotes.length > IntegrationMaxChar
                                        ? `Notes cannot be longer than ${IntegrationMaxChar} characters.`
                                        : undefined
                                }
                                maximumLength={IntegrationMaxChar}
                            />
                        </Detail>
                    </DetailsSection>
                </DetailsColumns>
            </SpinnerArea>
            {destructiveUpdateInfo && (
                <IntegrationStatusConfirmationModal
                    updateInfo={destructiveUpdateInfo}
                    onClose={onConfirmDestructiveSave}
                />
            )}
        </DetailsContainer>
    );
}
